import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { timesheetService } from '../services/TimesheetService';
import { Timesheet } from '../models/Timesheet';
import { saveFile } from '../utils/fileExport';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Calendar,
  Download,
} from 'lucide-react';

export const HRDashboard: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();
  const [pendingTimesheets, setPendingTimesheets] = useState<Timesheet[]>([]);
  const [approvedTimesheets, setApprovedTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    loadTimesheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const loadTimesheets = async () => {
    if (!company) {
      setLoading(false);
      return;
    }

    try {
      const [pending, approved] = await Promise.all([
        timesheetService.getTimesheetsByStatus(company.id, 'submitted'),
        timesheetService.getTimesheetsByStatus(company.id, 'approved'),
      ]);

      setPendingTimesheets(pending);
      setApprovedTimesheets(approved);
      setError(null);
    } catch (err) {
      console.error('Error loading timesheets:', err);
      setError('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (timesheetId: string) => {
    if (!user) return;

    try {
      await timesheetService.approveTimesheet(
        timesheetId,
        user.id,
        user.name,
        user.role
      );
      await loadTimesheets();
    } catch (err) {
      console.error('Error approving timesheet:', err);
      setError('Failed to approve timesheet');
    }
  };

  const handleReject = async () => {
    if (!selectedTimesheet || !user || !rejectReason.trim()) return;

    try {
      await timesheetService.rejectTimesheet(
        selectedTimesheet.id,
        user.id,
        rejectReason
      );
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedTimesheet(null);
      await loadTimesheets();
    } catch (err) {
      console.error('Error rejecting timesheet:', err);
      setError('Failed to reject timesheet');
    }
  };

  const handleMarkPaid = async (timesheetId: string) => {
    try {
      await timesheetService.markAsPaid(timesheetId);
      await loadTimesheets();
    } catch (err) {
      console.error('Error marking timesheet as paid:', err);
      setError('Failed to mark timesheet as paid');
    }
  };

  const handleExportCSV = async () => {
    try {
      const timesheetIds = approvedTimesheets.map(t => t.id);
      const csv = await timesheetService.exportToCSV(timesheetIds);

      // saveFile branches web (anchor download) vs native (Filesystem + Share).
      const blob = new Blob([csv], { type: 'text/csv' });
      await saveFile(
        `timesheets-${new Date().toISOString().split('T')[0]}.csv`,
        blob,
        'text/csv'
      );
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV');
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatHours = (hours: number) => {
    return hours.toFixed(2);
  };

  if (user?.role !== 'admin' && user?.role !== 'hr') {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        <div className="alert alert-error">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: '4px solid var(--color-gray-200)',
              borderTop: '4px solid var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ marginTop: 'var(--spacing-4)', color: 'var(--color-gray-600)' }}>
            Loading HR Dashboard...
          </p>
        </div>
    );
  }

  return (
    <>
      <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-6)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--spacing-2)' }}>
              HR Dashboard
            </h1>
            <p style={{ color: 'var(--color-gray-600)' }}>
              Manage timesheets and payroll
            </p>
          </div>
          {approvedTimesheets.length > 0 && (
            <button className="btn btn-primary" onClick={handleExportCSV}>
              <Download size={18} />
              Export to CSV
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-4)' }}>
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {/* Statistics */}
        <div className="grid-responsive" style={{ gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-6)' }}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <Clock size={32} color="var(--color-warning)" />
              <div>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                  Pending Approval
                </p>
                <p style={{ fontSize: 'var(--font-size-2xl)', fontWeight: '700' }}>
                  {pendingTimesheets.length}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <CheckCircle size={32} color="var(--color-success)" />
              <div>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                  Approved (Unpaid)
                </p>
                <p style={{ fontSize: 'var(--font-size-2xl)', fontWeight: '700' }}>
                  {approvedTimesheets.length}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <DollarSign size={32} color="var(--color-primary)" />
              <div>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                  Total Hours (Approved)
                </p>
                <p style={{ fontSize: 'var(--font-size-2xl)', fontWeight: '700' }}>
                  {formatHours(approvedTimesheets.reduce((sum, t) => sum + t.totalHours, 0))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Timesheets */}
        <div style={{ marginBottom: 'var(--spacing-6)' }}>
          <h2 style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--spacing-4)' }}>
            Pending Approval ({pendingTimesheets.length})
          </h2>

          {pendingTimesheets.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 'var(--spacing-6)' }}>
              <CheckCircle size={48} style={{ color: 'var(--color-gray-400)', margin: '0 auto var(--spacing-3)' }} />
              <p style={{ color: 'var(--color-gray-600)' }}>
                No timesheets pending approval
              </p>
            </div>
          ) : (
            <div className="grid" style={{ gap: 'var(--spacing-4)' }}>
              {pendingTimesheets.map(timesheet => (
                <div key={timesheet.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'var(--spacing-4)' }}>
                    <div>
                      <h3 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--spacing-1)' }}>
                        {timesheet.userName}
                      </h3>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                        {formatDate(timesheet.periodStart)} - {formatDate(timesheet.periodEnd)}
                      </p>
                      {timesheet.employeeId && (
                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                          Employee ID: {timesheet.employeeId}
                        </p>
                      )}
                    </div>
                    <span className="badge badge-warning">Pending</span>
                  </div>

                  <div className="grid-responsive" style={{ gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>Total Hours</p>
                      <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>
                        {formatHours(timesheet.totalHours)}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>Sessions</p>
                      <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>
                        {timesheet.timeEntries.length}
                      </p>
                    </div>
                    {timesheet.totalMileageReimbursement && timesheet.totalMileageReimbursement > 0 && (
                      <div>
                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>Mileage</p>
                        <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>
                          ${timesheet.totalMileageReimbursement.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                    <button
                      className="btn btn-success"
                      onClick={() => handleApprove(timesheet.id)}
                      style={{ flex: 1 }}
                    >
                      <CheckCircle size={18} />
                      Approve
                    </button>
                    <button
                      className="btn btn-error"
                      onClick={() => {
                        setSelectedTimesheet(timesheet);
                        setShowRejectModal(true);
                      }}
                      style={{ flex: 1 }}
                    >
                      <XCircle size={18} />
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Approved Timesheets */}
        <div>
          <h2 style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--spacing-4)' }}>
            Approved (Ready for Payroll) ({approvedTimesheets.length})
          </h2>

          {approvedTimesheets.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 'var(--spacing-6)' }}>
              <Calendar size={48} style={{ color: 'var(--color-gray-400)', margin: '0 auto var(--spacing-3)' }} />
              <p style={{ color: 'var(--color-gray-600)' }}>
                No approved timesheets awaiting payment
              </p>
            </div>
          ) : (
            <div className="grid" style={{ gap: 'var(--spacing-4)' }}>
              {approvedTimesheets.map(timesheet => (
                <div key={timesheet.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'var(--spacing-4)' }}>
                    <div>
                      <h3 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--spacing-1)' }}>
                        {timesheet.userName}
                      </h3>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                        {formatDate(timesheet.periodStart)} - {formatDate(timesheet.periodEnd)}
                      </p>
                    </div>
                    <span className="badge badge-success">Approved</span>
                  </div>

                  <div className="grid-responsive" style={{ gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>Total Hours</p>
                      <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>
                        {formatHours(timesheet.totalHours)}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>Sessions</p>
                      <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>
                        {timesheet.timeEntries.length}
                      </p>
                    </div>
                  </div>

                  <button
                    className="btn btn-primary"
                    onClick={() => handleMarkPaid(timesheet.id)}
                  >
                    <DollarSign size={18} />
                    Mark as Paid
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 'var(--spacing-4)' }}>Reject Timesheet</h2>
            <p style={{ marginBottom: 'var(--spacing-4)', color: 'var(--color-gray-600)' }}>
              Please provide a reason for rejecting this timesheet:
            </p>
            <textarea
              className="input"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              style={{ marginBottom: 'var(--spacing-4)', width: '100%' }}
            />
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setShowRejectModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-error"
                onClick={handleReject}
                disabled={!rejectReason.trim()}
              >
                <XCircle size={18} />
                Reject Timesheet
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
